import React, { useState, useRef, useEffect } from 'react';
import { Card, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { FaRobot, FaPaperPlane, FaTrash, FaCopy, FaDownload, FaImage, FaTimes } from 'react-icons/fa';
import axios from 'axios';

const AIMedicalAssistant = () => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I am your AI Medical Assistant. I can help you with:\n\n• Medicine information and interactions\n• Disease symptoms and treatments\n• Prescription recommendations\n• Medical report analysis\n• Dosage guidelines\n• Image analysis (upload medical images)\n\nHow can I assist you today?'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const quickPrompts = [
    'What are the side effects of Paracetamol?',
    'Symptoms of diabetes',
    'Drug interactions with Aspirin',
    'Treatment for hypertension',
    'Dosage for Amoxicillin 500mg',
    'Differential diagnosis for fever'
  ];

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() && !selectedImage) return;

    const userMessage = input.trim() || 'Analyze this image';
    setInput('');
    setError(null);

    setMessages(prev => [...prev, { 
      role: 'user', 
      content: userMessage,
      image: imagePreview
    }]);
    setLoading(true);

    try {
      let response;
      
      if (selectedImage) {
        const formData = new FormData();
        formData.append('image', selectedImage);
        formData.append('message', userMessage);
        formData.append('history', JSON.stringify(messages.slice(-10)));
        
        response = await axios.post('/api/ai-assistant.php', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        response = await axios.post('/api/ai-assistant.php', {
          message: userMessage,
          history: messages.slice(-10)
        });
      }

      console.log('Response received:', response.data);

      // Add AI response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response
      }]);
      
      // Log OCR debug info if available
      if (response.data.ocrDebug) {
        console.log('OCR Debug Info:', response.data.ocrDebug);
      }
      if (response.data.extractedText) {
        console.log('Extracted Text:', response.data.extractedText);
      }
      
      removeImage();
    } catch (err) {
      console.error('AI Assistant Error:', err);
      console.error('Full error response:', err.response);
      
      let errorMessage = 'Failed to get response. ';
      
      if (err.response?.data?.fullError) {
        console.error('Groq API Error:', err.response.data.fullError);
        errorMessage += JSON.stringify(err.response.data.fullError);
      } else if (err.response?.data?.error) {
        errorMessage += err.response.data.error;
      } else if (err.response?.data?.details) {
        errorMessage += err.response.data.details;
      } else if (err.message) {
        errorMessage += err.message;
      }
      
      setError(errorMessage);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleQuickPrompt = (prompt) => {
    setInput(prompt);
  };

  const clearChat = () => {
    setMessages([
      {
        role: 'assistant',
        content: 'Chat cleared. How can I assist you today?'
      }
    ]);
    setError(null);
  };

  const copyMessage = (content) => {
    navigator.clipboard.writeText(content);
    alert('Copied to clipboard!');
  };

  const exportChat = () => {
    const chatText = messages.map(msg => 
      `${msg.role === 'user' ? 'Doctor' : 'AI Assistant'}: ${msg.content}`
    ).join('\n\n');
    
    const blob = new Blob([chatText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `medical-consultation-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2>
                <FaRobot className="me-2 text-primary" />
                AI Medical Assistant
              </h2>
            </div>
            <div>
              <Button variant="outline-danger" size="sm" onClick={clearChat} className="me-2">
                <FaTrash className="me-1" /> Clear Chat
              </Button>
              <Button variant="outline-primary" size="sm" onClick={exportChat}>
                <FaDownload className="me-1" /> Export
              </Button>
            </div>
          </div>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Card className="shadow-sm">
            <Card.Body style={{ height: '60vh', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`d-flex mb-3 ${message.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
                >
                  <div
                    style={{
                      maxWidth: '75%',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      backgroundColor: message.role === 'user' ? '#007bff' : '#ffffff',
                      color: message.role === 'user' ? '#ffffff' : '#000000',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      position: 'relative'
                    }}
                  >
                    <div className="d-flex align-items-center mb-1">
                      <Badge bg={message.role === 'user' ? 'light' : 'primary'} className="me-2">
                        {message.role === 'user' ? '👨⚕️ Doctor' : '🤖 AI Assistant'}
                      </Badge>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        style={{ color: message.role === 'user' ? '#ffffff' : '#007bff' }}
                        onClick={() => copyMessage(message.content)}
                      >
                        <FaCopy size={12} />
                      </Button>
                    </div>
                    {message.image && (
                      <div className="mb-2">
                        <img 
                          src={message.image} 
                          alt="Uploaded" 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '200px', 
                            borderRadius: '8px',
                            border: '2px solid rgba(255,255,255,0.3)'
                          }} 
                        />
                      </div>
                    )}
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {message.content}
                    </div>
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="d-flex justify-content-start mb-3">
                  <div
                    style={{
                      padding: '12px 16px',
                      borderRadius: '12px',
                      backgroundColor: '#ffffff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>AI is thinking...</span>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </Card.Body>

            <Card.Footer className="bg-white">
              <div className="mb-3">
                <small className="text-muted d-block mb-2">Quick Questions:</small>
                <div className="d-flex flex-wrap gap-2">
                  {quickPrompts.map((prompt, index) => (
                    <Badge
                      key={index}
                      bg="light"
                      text="dark"
                      style={{ cursor: 'pointer', padding: '8px 12px' }}
                      onClick={() => handleQuickPrompt(prompt)}
                    >
                      {prompt}
                    </Badge>
                  ))}
                </div>
              </div>

              {imagePreview && (
                <div className="mb-2 position-relative d-inline-block">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    style={{ 
                      maxWidth: '150px', 
                      maxHeight: '150px', 
                      borderRadius: '8px',
                      border: '2px solid #007bff'
                    }} 
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    className="position-absolute top-0 end-0 m-1"
                    style={{ borderRadius: '50%', width: '25px', height: '25px', padding: '0' }}
                    onClick={removeImage}
                  >
                    <FaTimes size={12} />
                  </Button>
                </div>
              )}

              <Form onSubmit={handleSubmit}>
                <div className="d-flex gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleImageSelect}
                    style={{ display: 'none' }}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                    style={{ borderRadius: '20px' }}
                    title="Upload Image"
                  >
                    <FaImage />
                  </Button>
                  <Form.Control
                    type="text"
                    placeholder="Ask about medicines, diseases, treatments or upload an image..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    disabled={loading}
                    style={{ borderRadius: '20px' }}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={loading || (!input.trim() && !selectedImage)}
                    style={{ borderRadius: '20px', minWidth: '100px' }}
                  >
                    {loading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <>
                        <FaPaperPlane className="me-2" />
                        Send
                      </>
                    )}
                  </Button>
                </div>
              </Form>

              <small className="text-muted d-block mt-2">
                ⚠️ Disclaimer: This AI assistant provides general medical information only. 
                Always consult with qualified healthcare professionals for medical decisions.
              </small>
            </Card.Footer>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AIMedicalAssistant;
